import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useState, useEffect } from 'react'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { format, startOfDay, endOfDay } from 'date-fns'
import { useQueryState, parseAsString, parseAsBoolean } from 'nuqs'
import useQueryGetPlayerWithDeposit from '@/modules/manage-player/api/hooks/useQueryGetPlayerWithDeposit'
import PlayerWithDepositListing from '@/modules/manage-player/components/PlayerWithDeposit/components/PlayerWithDepositListing'

import type { ExportPlayerWithDepositCsvRequest } from '@/modules/manage-player/type'

type SortOrder = 'asc' | 'desc' | undefined

function PlayerWithDepositPage() {
	const { pageSize, currentPage, searchQuery, channelFilter } =
		useTablePagination()
	const { dateFilter, setDateFilter } = useDateFilter()
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(''),
	)
	const [excludeFreeCredit] = useQueryState(
		'excludeFreeCredit',
		parseAsBoolean.withOptions({ shallow: false }),
	)

	useEffect(() => {
		if (!dateFilter.startDate && !dateFilter.endDate && dateRange !== 'all') {
			const today = new Date()
			setDateFilter({
				startDate: format(startOfDay(today), 'yyyy-MM-dd'),
				endDate: format(endOfDay(today), 'yyyy-MM-dd'),
			})
			setDateRange('today')
		}
	}, [
		dateFilter.startDate,
		dateFilter.endDate,
		dateRange,
		setDateFilter,
		setDateRange,
	])

	const [depositAmountSort, setDepositAmountSort] = useState<SortOrder>('desc')
	const [depositTimeSort, setDepositTimeSort] = useState<SortOrder>()

	const handleSort = (columnId: string, order: SortOrder) => {
		if (columnId === 'totalDepositAmount') {
			setDepositAmountSort(order)
			setDepositTimeSort(undefined)
		} else if (columnId === 'totalDepositTime') {
			setDepositTimeSort(order)
			setDepositAmountSort(undefined)
		}
	}

	const payload = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		phone: searchQuery,
		channelName: channelFilter || undefined,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		depositAmountSort,
		depositTimeSort,
		excludeFreeCredit: excludeFreeCredit || undefined,
	}

	const { data, isLoading } = useQueryGetPlayerWithDeposit(payload)

	const exportParams = {
		phone: searchQuery,
		channelName: channelFilter || undefined,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		...(depositAmountSort && {
			depositAmountSort: depositAmountSort as 'asc' | 'desc',
		}),
		...(depositTimeSort && {
			depositTimeSort: depositTimeSort as 'asc' | 'desc',
		}),
		excludeFreeCredit: excludeFreeCredit || undefined,
	} as Omit<
		ExportPlayerWithDepositCsvRequest,
		'take' | 'skip' | 'filename' | 'columnMappings'
	>

	return (
		<PageContainer>
			<PlayerWithDepositListing
				data={data?.data || []}
				total={data?.total || 0}
				isLoading={isLoading}
				onSort={handleSort}
				exportParams={exportParams}
			/>
		</PageContainer>
	)
}

const GuardedPlayerWithDepositPage = withUserPermission(PlayerWithDepositPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_PLAYER],
})

export default GuardedPlayerWithDepositPage
